import { fromTable } from '@/integrations/supabase/db';
import { SearchResult, SearchSectionId } from '../types';
import { getMatchFunction, getServerPattern, getServerLimit, calculateRelevanceScore } from '../utils';

export async function searchEigenvertraege(
  term: string, 
  limit: number, 
  exactMatch: boolean,
  fuzzyMatch: boolean = false,
  signal?: AbortSignal
): Promise<SearchResult[]> {
  if (!term.trim()) return [];
  
  const pattern = getServerPattern(term, fuzzyMatch);
  const serverLimit = getServerLimit(limit, fuzzyMatch);
  const matchFn = getMatchFunction(exactMatch, fuzzyMatch);
  
  // Step 1: Find matching customers first (for name-based search)
  const { data: matchingKunden } = await fromTable('kunden')
    .select('id, vorname, zuname, firmenname')
    .or(`vorname.ilike.${pattern},zuname.ilike.${pattern},firmenname.ilike.${pattern}`)
    .limit(fuzzyMatch ? 200 : 100);
  
  const matchingKundenIds = (matchingKunden || []).map((k: any) => k.id);
  
  // Step 2: Query contracts by polizzennummer OR by matching customer IDs
  let query = fromTable('vertraege')
    .select('id, polizzennummer, status, sparte_id, gesellschaft_id, customer_id, updated_at');
  
  if (matchingKundenIds.length > 0) {
    query = query.or(`polizzennummer.ilike.${pattern},customer_id.in.(${matchingKundenIds.join(',')})`);
  } else {
    query = query.ilike('polizzennummer', pattern);
  }
  
  const { data, error } = await query.limit(serverLimit);

  if (error) {
    console.error('Eigenverträge search error:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // Step 3: Collect IDs for lookup queries
  const sparteIds = [...new Set(data.map(v => v.sparte_id).filter(Boolean))];
  const gesellschaftIds = [...new Set(data.map(v => v.gesellschaft_id).filter(Boolean))];
  const kundenIds = [...new Set(data.map(v => v.customer_id).filter(Boolean))];

  // Step 4: Parallel lookup queries
  const [spartenRes, gesellschaftenRes, kundenRes] = await Promise.all([
    sparteIds.length > 0 
      ? fromTable('sparten').select('id, sparte').in('id', sparteIds)
      : { data: [] },
    gesellschaftIds.length > 0 
      ? fromTable('gesellschaften').select('id, name').in('id', gesellschaftIds)
      : { data: [] },
    kundenIds.length > 0 
      ? fromTable('kunden').select('id, vorname, zuname, firmenname').in('id', kundenIds)
      : { data: [] },
  ]);

  // Step 5: Build lookup maps
  const sparteById = new Map((spartenRes.data || []).map((s: any) => [s.id, s.sparte]));
  const gesellschaftById = new Map((gesellschaftenRes.data || []).map((g: any) => [g.id, g.name]));
  const kundeById = new Map((kundenRes.data || []).map((k: any) => [k.id, k]));

  // Step 6: Client-side multi-term filtering and map results
  return data
    .map(v => {
      const kunde = kundeById.get(v.customer_id);
      const kundeName = kunde?.firmenname || `${kunde?.vorname || ''} ${kunde?.zuname || ''}`.trim();
      const sparteName = sparteById.get(v.sparte_id);
      const gesellschaftName = gesellschaftById.get(v.gesellschaft_id);
      
      return {
        contract: v,
        kundeName,
        sparteName,
        gesellschaftName
      };
    })
    .filter(item => matchFn(
      term,
      item.contract.polizzennummer,
      item.kundeName,
      item.sparteName,
      item.gesellschaftName
    ))
    .slice(0, limit)
    .map(item => {
      const title = item.contract.polizzennummer || 'Ohne Polizzennummer';
      const subtitle = [item.sparteName, item.gesellschaftName, item.kundeName].filter(Boolean).join(' • ');
      
      return {
        id: item.contract.id,
        type: 'eigenvertraege' as SearchSectionId,
        title,
        subtitle,
        badge: item.contract.status || 'Vertrag',
        data: { ...item.contract, leadhelden_lead: (item.contract as any).leadhelden_lead },
        relevanceScore: calculateRelevanceScore(term, title, subtitle, item.contract.updated_at)
      };
    });
}
